import { FC } from 'react';
import { useChartWidth, usePlotArea } from 'recharts';

/** Где стоит подпись величины: за концом полосы либо над столбцом. */
export type ChartValuePlacement = 'end' | 'top';

/** Отступ подписи величины от конца дорожки: у полосы он остаётся запасом в отведённом ей месте. */
export const VALUE_LABEL_GAP = 8;

/** Высота строки подписей величин над столбцами: в неё встаёт одна строка текста. */
export const VALUE_LABEL_ROW_HEIGHT = 20;

type Props = {
  /** Прямоугольник заполненной части элемента: библиотека подставляет его сама */
  viewBox?: { x?: number; y?: number; width?: number; height?: number };
  /** Отступ подписи от конца дорожки столбца; у полосы отступ заложен в отведённое место */
  offset?: number;
  /** Показанная величина */
  value?: string | number;
  /** Направление элементов диаграммы: по горизонтали подпись стоит за концом, по вертикали — над */
  placement?: ChartValuePlacement;
  /** Место элемента в ряду: библиотека подставляет его сама */
  index?: number;
  /** Сколько элементов в ряду: между ними делится ширина рисунка */
  count?: number;
  /** Ширина самой длинной величины диаграммы — с ней сравнивается место, доставшееся элементу */
  valueTextWidth?: number;
};

/**
 * Подпись величины за концом дорожки элемента.
 *
 * Величина стоит с того конца дорожки, где нет названия, — за концом самой дорожки, а не за концом
 * её заполненной части: иначе у короткой полосы величина ложится на дорожку и закрывает собой то,
 * сколько до потолка не хватает, а отведённое ей за дорожкой место остаётся пустым. Правило одно на
 * оба направления: по горизонтали величина за концом дорожки, по вертикали — над её концом.
 *
 * В отведённом ей месте величина прижата к его дальнему концу — так же, как величина в строке
 * показателя стандартного дашборда: величины ряда заканчиваются на одной черте, и балл читается по
 * разрядам, а не по началу строки. Прижим к началу места дал бы ту же черту с другой стороны, но
 * одноимённый показатель стандартного дашборда и собранная диаграмма разошлись бы видом.
 *
 * Подпись выведена своей разметкой, а не готовой подписью библиотеки: та отмеряет себе место до
 * края области рисования, и у дорожки во всю длину места не остаётся вовсе — величина переносится
 * на вторую строку, строка вырастает по высоте, а полосы соседних строк перестают стоять на одной
 * решётке. Место под величину отведено полем области рисования, поэтому подпись рисуется одной
 * строкой при любой своей длине.
 *
 * Там, где на элемент приходится меньше ширины, чем занимает сама величина, подписи разведены на
 * две строки через одну: в тесной карточке они иначе встают вплотную и читаются как одна. Через
 * строку подпись расходится с ближайшей соседкой по своей строке на два элемента. Укоротить
 * величину вместо этого нечем: усечённое число молча врёт, в отличие от усечённого названия.
 */
export const ChartValueLabel: FC<Props> = ({
  viewBox,
  offset = 0,
  value,
  placement = 'end',
  index = 0,
  count = 1,
  valueTextWidth = 0,
}) => {
  // Дорожка занимает всю область рисования, поэтому её конец берётся у самой области, а не у
  // заполненной части элемента. Дальний конец отведённого под величину места — край рисунка: место
  // отведено полем области рисования, и за полем ничего больше нет.
  const plotArea = usePlotArea();
  const chartWidth = useChartWidth();
  const text = value === undefined || value === null ? '' : String(value);
  if (!viewBox || !plotArea || chartWidth === undefined || text === '') return null;

  const x = viewBox.x ?? 0;
  const y = viewBox.y ?? 0;
  const width = viewBox.width ?? 0;
  const height = viewBox.height ?? 0;
  const isAbove = placement === 'top';
  const isCrowded = count > 0 && valueTextWidth > plotArea.width / count;
  const isRaised = isAbove && isCrowded && index % 2 === 1;

  return (
    <text
      x={isAbove ? x + width / 2 : chartWidth}
      y={isAbove ? plotArea.y - offset - (isRaised ? VALUE_LABEL_ROW_HEIGHT : 0) : y + height / 2}
      textAnchor={isAbove ? 'middle' : 'end'}
      dominantBaseline={isAbove ? 'auto' : 'central'}
      className="fill-foreground text-xs"
    >
      {text}
    </text>
  );
};
